import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownToLine, ArrowUpRight, CalendarDays, Download, Lightbulb, Loader2, TrendingUp } from "lucide-react";
import { MNOS, type MnoCode, type Order } from "@evocha/shared-types";
import { useDailySales, useOrders, useSummary } from "../lib/queries";
import { tzs } from "../lib/format";

const PERIODS = ["Today", "7 days", "30 days"] as const;
type Period = (typeof PERIODS)[number];

const PERIOD_CONFIG: Record<Period, { days: number; summary: "daily" | "weekly" | "monthly" }> = {
  Today: { days: 1, summary: "daily" },
  "7 days": { days: 7, summary: "weekly" },
  "30 days": { days: 30, summary: "monthly" },
};

function pct(part: number, total: number): number {
  if (!total) return 0;
  return Math.round((part / total) * 100);
}

function downloadCsv(filename: string, header: string[], rows: Array<Array<string | number>>) {
  const csv = [header.join(","), ...rows.map((r) => r.map((c) => (typeof c === "string" && c.includes(",") ? `"${c}"` : c)).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function Reports() {
  const [period, setPeriod] = useState<Period>("7 days");
  const [exported, setExported] = useState(false);
  const config = PERIOD_CONFIG[period];

  const summary = useSummary(config.summary);
  const daily = useDailySales(config.days);

  const from = daily.data?.from;
  const to = daily.data?.to;
  const orders = useOrders({ from, to, limit: 100 }, Boolean(from && to));

  const sales = summary.data?.sales;
  const gross = Number(sales?.gross_minor ?? 0);
  const commission = Number(summary.data?.commission_minor ?? 0);
  const successRate = sales?.count ? Math.round((sales.success / sales.count) * 100) : 0;
  const avgSale = Number(summary.data?.average_sale_minor ?? 0);

  const maxDay = Math.max(1, ...(daily.data?.days ?? []).map((d) => Number(d.gross_minor)));

  const networks = useMemo(() => {
    const rows = summary.data?.net_network ?? [];
    const total = rows.reduce((sum, n) => sum + Number(n.gross_minor), 0);
    const rate = gross ? commission / gross : 0.032;
    return rows.map((n) => {
      const code = String(n.mno) as MnoCode;
      const value = Number(n.gross_minor);
      return {
        code,
        name: MNOS[code]?.name ?? String(n.mno),
        colour: MNOS[code]?.colour ?? "#64748b",
        count: n.count,
        gross: value,
        share: pct(value, total),
        commission: Math.round(value * rate),
      };
    });
  }, [summary.data, gross, commission]);

  const totalGross = networks.reduce((sum, n) => sum + n.gross, 0);
  const totalCommission = networks.reduce((sum, n) => sum + n.commission, 0);

  const orderRows = orders.data?.data ?? [];

  const hours = useMemo(() => {
    const buckets = Array.from({ length: 24 }, () => 0);
    for (const o of orderRows as Order[]) {
      const hour = new Date(o.created_at).getHours();
      buckets[hour] = (buckets[hour] ?? 0) + 1;
    }
    const max = Math.max(1, ...buckets);
    return buckets.map((value, hour) => ({ hour, value: Math.round((value / max) * 100), count: value, raw: value }));
  }, [orderRows]);

  const visibleHours = hours.filter((h) => h.hour >= 6 && h.hour <= 22);

  const popular = useMemo(() => {
    const counts = new Map<number, number>();
    for (const o of orderRows) {
      const amount = Number(o.amount_minor);
      counts.set(amount, (counts.get(amount) ?? 0) + 1);
    }
    const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
    const max = Math.max(1, ...sorted.map(([, count]) => count));
    return sorted.map(([amount, count], i) => ({
      amount,
      count,
      width: Math.round((count / max) * 100),
      rank: i + 1,
    }));
  }, [orderRows]);

  function exportReport() {
    const days = daily.data?.days ?? [];
    downloadCsv(
      `evocha-${period.replace(/\s/g, "-").toLowerCase()}-report.csv`,
      ["Date", "Orders", "Gross (TZS)", "Commission (TZS)"],
      days.map((d) => [d.date, d.orders, (Number(d.gross_minor) / 100).toFixed(2), (Number(d.commission_minor) / 100).toFixed(2)]),
    );
    setExported(true);
    window.setTimeout(() => setExported(false), 3000);
  }

  const loading = summary.isLoading || daily.isLoading;

  return (
    <div className="stack-6">
      <div className="page-head">
        <div>
          <h2 className="ev-h2">Reports</h2>
          <p className="ev-muted">See which networks, amounts and hours make you the most commission.</p>
        </div>
        <div className="page-head-actions">
          <div className="segmented segmented-lg">
            {PERIODS.map((p) => (
              <button key={p} className={period === p ? "segmented-on" : ""} onClick={() => setPeriod(p)}>{p}</button>
            ))}
          </div>
          <button className="ev-btn ev-btn-sm ev-btn-secondary" onClick={exportReport} disabled={!daily.data}>
            <Download size={15} /> {exported ? "Exported" : "Export"}
          </button>
        </div>
      </div>

      <div className="kpi-grid">
        <Kpi label="Gross sales" value={loading ? "…" : tzs(gross)} delta={`${sales?.count ?? 0} transactions`} hint={`${sales?.success ?? 0} sent`} />
        <Kpi label="Commission" value={loading ? "…" : tzs(commission)} delta={`${pct(commission, gross)}% average`} hint="on successful sales" />
        <Kpi label="Success rate" value={loading ? "…" : `${successRate}%`} delta={`${sales?.failed ?? 0} failed`} hint={`${sales?.pending ?? 0} pending`} />
        <Kpi label="Avg. sale" value={loading ? "…" : tzs(avgSale)} delta={`${sales?.review ?? 0} under review`} hint="per successful sale" />
      </div>

      <div className="report-grid">
        <section className="ev-card ev-card-pad">
          <div className="ev-row-between">
            <div>
              <span className="ev-eyebrow">Sales trend</span>
              <h3 className="ev-h3" style={{ marginTop: 4 }}>Gross value by day</h3>
            </div>
            <span className="chart-legend"><span className="chart-dot" /> TZS value</span>
          </div>
          {daily.isLoading ? (
            <div className="ev-empty"><Loader2 size={20} className="spin" /></div>
          ) : (
            <div className="bar-chart bar-chart-tall" role="img" aria-label="Gross sales by day">
              {(daily.data?.days ?? []).map((day) => {
                const value = Number(day.gross_minor);
                return (
                  <div className="bar-col" key={day.date}>
                    <span className="bar-value">{value ? `${Math.round(value / 100_000) / 10}k` : "0"}</span>
                    <div className="bar-track">
                      <span
                        className={value === maxDay && maxDay > 0 ? "bar bar-peak" : "bar"}
                        style={{ height: `${Math.max((value / maxDay) * 100, value ? 3 : 0)}%` }}
                      />
                    </div>
                    <span className="bar-day">{new Date(`${day.date}T00:00:00Z`).toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" })}</span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="chart-foot">
            <span><strong>{tzs(daily.data?.gross_minor ?? 0)}</strong> for {period.toLowerCase()}</span>
            <span className="ev-badge ev-badge-neutral">{daily.data?.orders ?? 0} orders</span>
          </div>
        </section>

        <section className="ev-card ev-card-pad">
          <div className="ev-row-between">
            <div>
              <span className="ev-eyebrow">Busiest hours</span>
              <h3 className="ev-h3" style={{ marginTop: 4 }}>When your customers buy</h3>
            </div>
            <CalendarDays size={18} className="ev-muted" />
          </div>
          <div className="hour-chart">
            {visibleHours.map((h) => (
              <div className="hour-col" key={h.hour}>
                <div className="hour-track"><span style={{ height: `${h.value}%`, opacity: h.count ? 1 : 0.25 }} /></div>
                <span className="hour-label">{String(h.hour).padStart(2, "0")}</span>
              </div>
            ))}
          </div>
          <div className="insight">
            <Lightbulb size={16} />
            <p>
              {hours.some((h) => h.count > 0)
                ? <>Your busiest hour is <strong>{String(hours.reduce((a, b) => (b.count > a.count ? b : a)).hour).padStart(2, "0")}:00</strong>. Keep enough balance available before the rush.</>
                : <>No sales in this period yet — hourly patterns appear as soon as you start selling.</>}
            </p>
          </div>
        </section>
      </div>

      <section className="ev-card">
        <div className="ev-card-head">
          <div>
            <h3 className="ev-card-title">Network performance</h3>
            <p className="ev-caption">Commission is estimated from your average rate on successful sales.</p>
          </div>
          <span className="ev-badge ev-badge-neutral">{networks.length} networks</span>
        </div>
        <div className="ev-table-wrap">
          <table className="ev-table network-table">
            <thead>
              <tr>
                <th>Network</th>
                <th className="ev-num">Sales</th>
                <th className="ev-num">Gross</th>
                <th className="ev-num">Share</th>
                <th className="ev-num">Commission</th>
              </tr>
            </thead>
            <tbody>
              {networks.length === 0 && (
                <tr><td colSpan={5} className="ev-muted" style={{ textAlign: "center", padding: 24 }}>No successful sales in this period.</td></tr>
              )}
              {networks.map((n) => (
                <tr key={n.code}>
                  <td>
                    <span className="network-cell">
                      <span className="mno-dot" style={{ background: n.colour }} />
                      {n.name}
                    </span>
                  </td>
                  <td className="ev-num">{n.count}</td>
                  <td className="ev-num ev-money">{tzs(n.gross)}</td>
                  <td className="ev-num">
                    <span className="share-cell">
                      <span className="share-track"><span style={{ width: `${n.share}%`, background: n.colour }} /></span>
                      {n.share}%
                    </span>
                  </td>
                  <td className="ev-num ev-pos">+{tzs(n.commission)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td><strong>Total</strong></td>
                <td className="ev-num"><strong>{networks.reduce((s, n) => s + n.count, 0)}</strong></td>
                <td className="ev-num ev-money">{tzs(totalGross)}</td>
                <td className="ev-num">100%</td>
                <td className="ev-num ev-pos">+{tzs(totalCommission)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <div className="report-grid report-grid-3">
        <section className="ev-card ev-card-pad">
          <span className="ev-eyebrow">Commission mix</span>
          <h3 className="ev-h3" style={{ marginTop: 4 }}>Where earnings come from</h3>
          <div className="commission-list">
            {networks.length === 0 && <p className="ev-caption">No commission yet.</p>}
            {networks.map((n) => (
              <div key={n.code}>
                <div className="ev-row-between">
                  <span className="commission-name"><span className="mno-dot" style={{ background: n.colour }} /> {n.name}</span>
                  <strong className="ev-tabular">{tzs(n.commission)}</strong>
                </div>
                <div className="mix-track"><span style={{ width: `${pct(n.commission, totalCommission)}%`, background: n.colour }} /></div>
              </div>
            ))}
          </div>
        </section>

        <section className="ev-card ev-card-pad">
          <span className="ev-eyebrow">Best sellers</span>
          <h3 className="ev-h3" style={{ marginTop: 4 }}>Popular amounts</h3>
          {popular.length === 0 ? (
            <p className="ev-caption" style={{ marginTop: 10 }}>No sales to rank yet.</p>
          ) : (
            <ol className="rank-list">
              {popular.map((r) => (
                <li key={r.amount}>
                  <span className="rank-index">{r.rank}</span>
                  <span className="rank-label">{tzs(r.amount)}</span>
                  <span className="rank-track"><span style={{ width: `${r.width}%` }} /></span>
                  <span className="rank-count">{r.count}</span>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="ev-card ev-card-pad">
          <span className="ev-eyebrow">Watch list</span>
          <h3 className="ev-h3" style={{ marginTop: 4 }}>Things to know</h3>
          <ul className="watch-list">
            {networks[0] && (
              <li><span className="watch-dot watch-mint" /><div><strong>{networks[0].name} is your engine</strong><p>{networks[0].share}% of gross sales and the highest commission.</p></div></li>
            )}
            {(sales?.pending ?? 0) + (sales?.review ?? 0) > 0 && (
              <li><span className="watch-dot watch-amber" /><div><strong>{(sales?.pending ?? 0) + (sales?.review ?? 0)} sale(s) awaiting resolution</strong><p>We are checking with the network. Do not repeat them.</p></div></li>
            )}
            <li><span className="watch-dot watch-blue" /><div><strong>Keep float ready</strong><p>Top up before busy hours so you never turn a customer away.</p></div></li>
          </ul>
          <button className="ev-btn ev-btn-sm ev-btn-ghost" style={{ marginTop: 4 }} onClick={() => setPeriod("30 days")}>Open 30-day view <ArrowUpRight size={14} /></button>
        </section>
      </div>

      <div className="export-bar">
        <ArrowDownToLine size={18} />
        <div>
          <strong>Need this for your accountant?</strong>
          <span>Export the daily sales series as CSV. Statements give you the full ledger.</span>
        </div>
        <button className="ev-btn ev-btn-sm ev-btn-secondary" onClick={exportReport} disabled={!daily.data}>Sales CSV</button>
        <Link className="ev-btn ev-btn-sm ev-btn-primary" to="/statements">Open statements</Link>
      </div>
    </div>
  );
}

function Kpi({ label, value, delta, hint }: { label: string; value: string; delta: string; hint: string }) {
  return (
    <section className="ev-card kpi-card">
      <span className="ev-eyebrow">{label}</span>
      <strong className="kpi-value">{value}</strong>
      <span className="kpi-foot">
        <span className="kpi-delta"><TrendingUp size={13} /> {delta}</span>
        <span className="ev-caption">{hint}</span>
      </span>
    </section>
  );
}
